import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Search, Plus, Trash2, Edit2, Check, X, Loader2,
  Package, DollarSign, AlertTriangle, Shield, CheckCircle,
  ChevronDown, ChevronUp, ExternalLink, Info, Download,
  FileText, UserPlus, Send, ArrowRightLeft, Award, Truck, Sparkles, RefreshCw,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';
import InfoTip from '@/components/InfoTip';
import { CPNBadge } from '@/hooks/useCPN';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';

const riskColor = (r) => ({
  Low: 'bg-green-100 text-green-800',
  Medium: 'bg-yellow-100 text-yellow-800',
  High: 'bg-red-100 text-red-800',
}[r] || 'bg-gray-100 text-gray-500');

const lifecycleColor = (s) => ({
  Active: 'bg-green-100 text-green-800',
  NRND: 'bg-yellow-100 text-yellow-800',
  LTB: 'bg-orange-100 text-orange-800',
  Obsolete: 'bg-red-100 text-red-800',
  EOL: 'bg-red-100 text-red-800',
}[s] || 'bg-gray-100 text-gray-500');

const confidenceColor = (c) => ({
  exact: 'bg-green-100 text-green-800',
  fuzzy: 'bg-yellow-100 text-yellow-800',
  unmatched: 'bg-red-100 text-red-800',
}[c] || 'bg-gray-100 text-gray-500');

const gradeColor = (g) => ({
  A: 'text-green-700 bg-green-50 border-green-300',
  B: 'text-blue-700 bg-blue-50 border-blue-300',
  C: 'text-yellow-700 bg-yellow-50 border-yellow-300',
  D: 'text-orange-700 bg-orange-50 border-orange-300',
  F: 'text-red-700 bg-red-50 border-red-300',
}[g] || 'text-gray-700 bg-gray-50 border-gray-300');

/* ──── Config Summary ──── */
const ConfigSummary = ({ project, collapsed, setCollapsed }) => (
  <Card className="bg-white border-gray-200 shadow-sm">
    <CardContent className="py-3 px-5">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-4 text-sm">
          <span className="font-semibold text-gray-900">{project.name}</span>
          <Badge variant="outline" className={project.workflow === 'new_design' ? 'text-blue-700 border-blue-200' : 'text-green-700 border-green-200'}>
            {project.workflow === 'new_design' ? 'New Design' : 'Validation'}
          </Badge>
          <span className="text-gray-400">|</span>
          <span className="text-gray-500">{project.target_volume?.toLocaleString()} units</span>
          {project.target_unit_cost && <span className="text-gray-500">Target: ${project.target_unit_cost}</span>}
          {project.display_currency !== 'USD' && <Badge variant="outline">{project.display_currency}</Badge>}
        </div>
        {collapsed ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronUp className="h-4 w-4 text-gray-400" />}
      </div>
      {!collapsed && (
        <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">Category:</span> <span className="font-medium">{project.product_category || 'Not set'}</span></div>
          <div><span className="text-gray-500">Market:</span> <span className="font-medium">{project.target_market_region || 'Not set'}</span></div>
          <div><span className="text-gray-500">Sourcing:</span> <span className="font-medium">{project.sourcing_region}</span></div>
          <div>
            <span className="text-gray-500">Compliance:</span>{' '}
            {project.compliance_frameworks?.length > 0
              ? project.compliance_frameworks.map(c => <Badge key={c} variant="outline" className="text-xs ml-1">{c.toUpperCase()}</Badge>)
              : <span className="text-gray-400">None</span>}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

/* ──── Health Scorecard ──── */
const HealthScorecard = ({ scorecard, components, project }) => {
  const sc = scorecard || {};
  const matched = components.filter(c => c.matched);
  const totalCost = matched.reduce((s, c) => s + (c.unit_price || 0) * (c.quantity || 1), 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-3" data-testid="health-scorecard">
      {/* BOM Grade */}
      <Card className={`shadow-sm border-2 ${gradeColor(sc.grade)}`}>
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] uppercase font-medium opacity-70">BOM Health</p>
          <p className="text-3xl font-black">{sc.grade || 'N/A'}</p>
          <p className="text-xs opacity-70">{sc.score || 0}/100</p>
        </CardContent>
      </Card>
      {/* Match Rate */}
      <Card className="bg-white shadow-sm">
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase flex items-center justify-center gap-0.5">Match Rate <InfoTip text="% of MPNs successfully matched to known components" /></p>
          <p className="text-2xl font-bold text-gray-900">{sc.match_rate || 0}%</p>
          <p className="text-[10px] text-gray-400">{sc.matched_count || 0}/{sc.total || 0}</p>
        </CardContent>
      </Card>
      {/* Lifecycle Risk */}
      <Card className={`shadow-sm ${(sc.lifecycle_risk_pct || 0) > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-white'}`}>
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase flex items-center justify-center gap-0.5">Lifecycle Risk <InfoTip text="% of parts with NRND, EOL, or Obsolete lifecycle status" /></p>
          <p className={`text-2xl font-bold ${(sc.lifecycle_risk_pct || 0) > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>{sc.lifecycle_risk_pct || 0}%</p>
          <p className="text-[10px] text-gray-400">{sc.lifecycle_risk_count || 0} parts</p>
        </CardContent>
      </Card>
      {/* Compliance */}
      <Card className="bg-white shadow-sm">
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase flex items-center justify-center gap-0.5">Compliance <InfoTip text="% of parts that are both EU-RoHS and REACH compliant" /></p>
          <p className={`text-2xl font-bold ${(sc.compliance_pct || 0) >= 90 ? 'text-green-700' : 'text-gray-900'}`}>{sc.compliance_pct || 0}%</p>
          <p className="text-[10px] text-gray-400">{sc.compliant_count || 0}/{sc.total || 0}</p>
        </CardContent>
      </Card>
      {/* High Risk */}
      <Card className={`shadow-sm ${(sc.high_risk_pct || 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase">High Risk</p>
          <p className={`text-2xl font-bold ${(sc.high_risk_pct || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>{sc.high_risk_pct || 0}%</p>
          <p className="text-[10px] text-gray-400">{sc.high_risk_count || 0} parts</p>
        </CardContent>
      </Card>
      {/* Single Source */}
      <Card className="bg-white shadow-sm">
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Single Source</p>
          <p className="text-2xl font-bold text-gray-900">{sc.single_source_pct || 0}%</p>
          <p className="text-[10px] text-gray-400">{sc.single_source_count || 0} parts</p>
        </CardContent>
      </Card>
      {/* BOM Cost */}
      <Card className="bg-white shadow-sm">
        <CardContent className="py-3 px-4 text-center">
          <p className="text-[10px] text-gray-500 uppercase">Unit BOM Cost</p>
          <p className="text-2xl font-bold text-gray-900">${Math.round(totalCost).toLocaleString()}</p>
          <p className="text-[10px] text-gray-400">{matched.length} priced</p>
        </CardContent>
      </Card>
    </div>
  );
};

/* ──── Search Panel (Workflow A) ──── */
const SearchPanel = ({ onAdd }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [refDes, setRefDes] = useState('');
  const [addingMpn, setAddingMpn] = useState(null);

  const doSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/design/search?q=${encodeURIComponent(query)}`);
      setResults(res.data.results || []);
    } catch { toast.error('Search failed'); }
    setSearching(false);
  }, [query]);

  const handleAdd = async (comp) => {
    setAddingMpn(comp.mpn);
    await onAdd(comp.mpn, quantity, refDes);
    setAddingMpn(null);
    setRefDes('');
    setQuantity(1);
  };

  return (
    <Card className="bg-white border-gray-200 shadow-sm border-l-4 border-l-blue-500">
      <CardContent className="pt-4 space-y-6">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Search by MPN, manufacturer, description, or category..." className="flex-1" data-testid="component-search-input" />
          <Button onClick={doSearch} disabled={searching} size="sm" className="bg-blue-600 text-white" data-testid="btn-search">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
        {results.length > 0 && (
          <div className="max-h-72 overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 text-xs">
                  <TableHead>MPN</TableHead><TableHead>Manufacturer</TableHead><TableHead>Category</TableHead>
                  <TableHead>Price (1K)</TableHead><TableHead>Lifecycle</TableHead><TableHead>Risk</TableHead><TableHead>Stock</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={i} className="text-sm" data-testid={`search-result-${i}`}>
                    <TableCell className="font-mono font-medium text-sm">
                      <div className="flex items-center gap-1.5">{r.mpn}{r.cpn_match && <Badge className="text-xs bg-purple-100 text-purple-700">CPN</Badge>}</div>
                    </TableCell>
                    <TableCell className="text-gray-600 text-xs">{r.manufacturer}</TableCell>
                    <TableCell className="text-gray-600 text-xs">{r.subcategory}</TableCell>
                    <TableCell className="font-medium">${r.pricing?.qty_1000?.toFixed(3)}</TableCell>
                    <TableCell><Badge className={`text-xs ${lifecycleColor(r.lifecycle)}`}>{r.lifecycle}</Badge></TableCell>
                    <TableCell><Badge className={`text-xs ${riskColor(r.risk_score)}`}>{r.risk_score}</Badge></TableCell>
                    <TableCell className="text-xs text-gray-500">{r.stock?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1 text-blue-600"
                        onClick={() => handleAdd(r)} disabled={addingMpn === r.mpn} data-testid={`add-component-${i}`}>
                        {addingMpn === r.mpn ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {results.length > 0 && (
          <div className="flex items-center gap-3 pt-1">
            <div className="flex items-center gap-1">
              <Label className="text-xs text-gray-500">Qty:</Label>
              <Input type="number" value={quantity} onChange={e => setQuantity(parseInt(e.target.value) || 1)} className="w-20 h-7 text-xs" data-testid="input-add-qty" />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs text-gray-500">Ref Des:</Label>
              <Input value={refDes} onChange={e => setRefDes(e.target.value)} placeholder="e.g., U1" className="w-28 h-7 text-xs" data-testid="input-add-refdes" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


/* ──── Parametric Comparison Component ──── */
const ParametricCompare = ({ components, compareSelection, toggleCompare, navigate }) => {
  const comps = components.filter(c => c.matched);
  const selected = comps.filter(c => compareSelection.includes(c.mpn));
  const PARAMS = [
    { key: 'category', label: 'Category', fff: false },
    { key: 'manufacturer', label: 'Manufacturer', fff: false },
    { key: 'lifecycle', label: 'Lifecycle', fff: false },
    { key: 'package_case', label: 'Package / Case', fff: true },
    { key: 'temperature_grade', label: 'Temperature Grade', fff: false },
    { key: 'temperature_range', label: 'Temperature Range', fff: false },
    { key: 'supply_voltage', label: 'Supply Voltage', fff: false },
    { key: 'power_rating', label: 'Power Rating', fff: false },
    { key: 'tolerance', label: 'Tolerance', fff: false },
    { key: 'value', label: 'Value / Rating', fff: true },
    { key: 'eu_rohs', label: 'EU-RoHS', fff: false, bool: true },
    { key: 'reach', label: 'REACH', fff: false, bool: true },
    { key: 'qualifications', label: 'Qualifications', fff: false },
    { key: 'median_price', label: 'Median Price', fff: false, price: true },
    { key: 'lead_time_weeks', label: 'Lead Time (wks)', fff: false },
  ];
  const getVal = (c, key) => {
    const comp = c.compliance || {};
    if (key === 'eu_rohs') return comp.eu_rohs ? 'Yes' : 'No';
    if (key === 'reach') return comp.reach ? 'Yes' : 'No';
    return c[key] || '-';
  };
  const fmtVal = (v, p) => {
    if (p.price && typeof v === 'number') return `$${v.toFixed(4)}`;
    if (p.bool) return v;
    return v || '-';
  };

  if (selected.length === 0) {
    return (
      <div className="space-y-4 py-4">
        <p className="text-sm text-gray-600 font-medium">Select up to 3 parts to compare with their alternates parametrically.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {comps.map(c => (
            <button key={c.mpn} onClick={() => toggleCompare(c.mpn)}
              className={`p-3 border rounded-lg text-left text-xs transition-all ${compareSelection.includes(c.mpn) ? 'border-green-500 bg-green-50 ring-1 ring-green-300' : 'border-gray-200 hover:border-gray-400'}`}
              data-testid={`param-select-${c.mpn}`}>
              <span className="font-mono font-medium block truncate">{c.mpn}</span>
              <span className="text-gray-400 block truncate">{c.manufacturer || c.make || '-'}</span>
              {c.category && <Badge variant="outline" className="text-[9px] mt-1">{c.category}</Badge>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Build comparison: original + up to 3 alternates per selection
  const groups = selected.map(orig => {
    const alts = (orig.alternates || []).slice(0, 3).map(a => ({
      ...a, mpn: a.mpn || 'N/A', _isAlt: true,
      compliance: { eu_rohs: a.eu_rohs !== undefined ? a.eu_rohs : true, reach: a.reach !== undefined ? a.reach : true },
      temperature_grade: a.temperature_grade || orig.temperature_grade,
      temperature_range: a.temperature_range || orig.temperature_range,
      package_case: a.package_case || orig.package_case,
      value: a.value || orig.value,
    }));
    return { original: orig, alternates: alts };
  });

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-medium text-gray-700">Comparing {selected.length} part(s):</p>
        {selected.map(c => (
          <Badge key={c.mpn} className="bg-green-100 text-green-800 text-xs gap-1">
            {c.mpn} <button onClick={() => toggleCompare(c.mpn)} className="ml-1 hover:text-red-600">&times;</button>
          </Badge>
        ))}
        {selected.length < 3 && <span className="text-xs text-gray-400">(select up to {3 - selected.length} more)</span>}
      </div>
      {groups.map(({ original, alternates }) => (
        <Card key={original.mpn} className="border-gray-200">
          <CardHeader className="py-2 px-4 border-b bg-gray-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="font-mono">{original.mpn}</span>
              <span className="text-gray-400 font-normal">vs {alternates.length} alternate(s)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 text-xs">
                    <TableHead className="font-semibold text-xs w-40">Parameter</TableHead>
                    <TableHead className="font-semibold text-xs text-blue-700 bg-blue-50/50">Original</TableHead>
                    {alternates.map((a, i) => <TableHead key={i} className="font-semibold text-xs">Alt {i + 1}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="text-xs bg-gray-50/50">
                    <TableCell className="font-medium text-gray-600">MPN</TableCell>
                    <TableCell className="font-mono font-medium text-blue-700 bg-blue-50/30">
                      <button className="hover:underline" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(original.mpn)}`)}>{original.mpn}</button>
                    </TableCell>
                    {alternates.map((a, i) => <TableCell key={i} className="font-mono text-xs">
                      {a.mpn !== 'N/A' ? <button className="hover:underline text-blue-600" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(a.mpn)}`)}>{a.mpn}</button> : '-'}
                    </TableCell>)}
                  </TableRow>
                  {PARAMS.map(p => {
                    const origVal = fmtVal(getVal(original, p.key), p);
                    return (
                      <TableRow key={p.key} className="text-xs">
                        <TableCell className="font-medium text-gray-600 flex items-center gap-1">
                          {p.label} {p.fff && <Badge className="text-[8px] bg-purple-100 text-purple-800 px-1">FFF</Badge>}
                        </TableCell>
                        <TableCell className="bg-blue-50/30 font-medium">{origVal}</TableCell>
                        {alternates.map((a, i) => {
                          const altVal = fmtVal(getVal(a, p.key), p);
                          const match = String(altVal) === String(origVal);
                          const fffMismatch = p.fff && !match && origVal !== '-' && altVal !== '-';
                          return (
                            <TableCell key={i} className={fffMismatch ? 'bg-red-50' : match ? 'bg-green-50/50' : ''}>
                              <span className={fffMismatch ? 'text-red-700 font-semibold' : match ? 'text-green-700' : ''}>
                                {altVal}
                              </span>
                              {fffMismatch && <span className="text-[9px] text-red-500 ml-1">(mismatch)</span>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


/* ──── Enriched BOM Table ──── */
const BOMTable = ({ components, onRemove, onEdit, showConfidence, onSelectForRfq, selectedRfq }) => {
  const navigate = useNavigate();
  const [editIdx, setEditIdx] = useState(-1);
  const [editQty, setEditQty] = useState(0);
  const [editRef, setEditRef] = useState('');
  const [viewTab, setViewTab] = useState('general');

  const startEdit = (idx, comp) => { setEditIdx(idx); setEditQty(comp.quantity || 1); setEditRef(comp.ref_designator || ''); };
  const saveEdit = () => { onEdit(editIdx, { quantity: editQty, ref_designator: editRef }); setEditIdx(-1); };

  if (components.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Package className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No components yet</p>
        <p className="text-sm mt-1">Search and add components above to build your BOM</p>
      </div>
    );
  }

  const tabHeaders = {
    general: ['#', showConfidence && 'Match', 'MPN / CPN', 'Make', 'Description', 'Lifecycle', 'YTEOL', 'Qty', 'Ref Des', 'Median Price', 'Datasheet', 'Actions'].filter(Boolean),
    risk: ['#', 'MPN', 'Lifecycle', 'YTEOL', 'Supply', 'Single Source', 'Compliance', 'Geopolitical', 'Lead Time', 'Overall'],
    compliance: ['#', 'MPN', 'Env. Compliance', 'EU-RoHS', 'CN-RoHS', 'REACH', 'PFAS', 'Qualifications', 'Temp Grade', 'Temp Range'],
    alternates: ['#', 'MPN', 'Alt MPN', 'Alt Manufacturer', 'Alt Type', 'EU-RoHS', 'CN-RoHS', 'REACH', 'PFAS', 'Temp Grade', 'Temp Range', 'Qualifications', 'Alt Price', '% Diff'],
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-1 border-b pb-1">
        {[
          { k: 'general', label: 'General' },
          { k: 'risk', label: 'Risk' },
          { k: 'compliance', label: 'Compliance' },
          { k: 'alternates', label: 'Alternates' },
        ].map(t => (
          <button key={t.k} onClick={() => setViewTab(t.k)}
            className={`px-3 py-1 text-xs font-medium rounded-t transition-colors ${viewTab === t.k ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`bom-view-${t.k}`}>{t.label}</button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 text-xs">
              {tabHeaders[viewTab].map((h, i) => <TableHead key={i} className="text-xs whitespace-nowrap">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {components.map((c, idx) => {
              const comp = c.compliance || {};
              const rd = c.risk_details || {};
              const alt = (c.alternates || [])[0] || {};
              if (viewTab === 'general') return (
                <TableRow key={idx} className={`text-sm ${!c.matched ? 'bg-red-50/50' : ''}`} data-testid={`bom-row-${idx}`}>
                  <TableCell className="text-xs text-gray-400">{idx + 1}</TableCell>
                  {showConfidence && <TableCell><Badge className={`text-[10px] ${confidenceColor(c.match_confidence)}`}>{c.match_confidence === 'exact' ? 'Exact' : c.match_confidence === 'fuzzy' ? 'Fuzzy' : 'No Match'}</Badge></TableCell>}
                  <TableCell className="font-mono font-medium text-sm">
                    <div className="flex flex-col gap-0.5">
                      {c.matched ? <button className="text-blue-600 hover:underline text-left" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(c.mpn)}`)}>{c.mpn}</button> : c.mpn}
                      <CPNBadge mpn={c.mpn} />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-gray-600">{c.manufacturer || '-'}</TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-[200px] truncate" title={c.description}>{c.description || '-'}</TableCell>
                  <TableCell>{c.matched ? <Badge className={`text-[10px] ${lifecycleColor(c.lifecycle)}`}>{c.lifecycle || '-'}</Badge> : <span className="text-xs text-gray-400">-</span>}</TableCell>
                  <TableCell className="text-xs">{c.matched && c.yteol ? <span className={c.yteol <= 24 ? 'text-red-600 font-semibold' : c.yteol <= 60 ? 'text-yellow-600 font-medium' : 'text-gray-600'}>{c.yteol}mo</span> : <span className="text-gray-400">-</span>}</TableCell>
                  <TableCell>{editIdx === idx ? <Input type="number" value={editQty} onChange={e => setEditQty(parseInt(e.target.value) || 1)} className="w-16 h-7 text-xs" /> : (c.quantity || 1)}</TableCell>
                  <TableCell>{editIdx === idx ? <Input value={editRef} onChange={e => setEditRef(e.target.value)} className="w-20 h-7 text-xs" /> : <span className="text-xs text-gray-500">{c.ref_designator || '-'}</span>}</TableCell>
                  <TableCell className="font-medium text-xs">{c.matched ? `$${(c.median_price || 0).toFixed(4)}` : '-'}</TableCell>
                  <TableCell>{c.datasheet_url ? <a href={c.datasheet_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-0.5"><ExternalLink className="h-3 w-3" />PDF</a> : <span className="text-xs text-gray-400">-</span>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {editIdx === idx ? (<><Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600" onClick={saveEdit}><Check className="h-3 w-3" /></Button><Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400" onClick={() => setEditIdx(-1)}><X className="h-3 w-3" /></Button></>
                      ) : (<><Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600" onClick={() => startEdit(idx, c)} data-testid={`edit-btn-${idx}`}><Edit2 className="h-3 w-3" /></Button><Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => onRemove(idx)} data-testid={`remove-btn-${idx}`}><Trash2 className="h-3 w-3" /></Button></>)}
                      {onSelectForRfq && c.matched && <input type="checkbox" checked={selectedRfq?.includes(c.mpn)} onChange={() => onSelectForRfq(c.mpn)} className="h-3 w-3 ml-1" title="Select for RFQ" />}
                    </div>
                  </TableCell>
                </TableRow>
              );
              if (viewTab === 'risk') return (
                <TableRow key={idx} className={`text-sm ${!c.matched ? 'bg-red-50/50' : ''}`} data-testid={`bom-risk-${idx}`}>
                  <TableCell className="text-xs text-gray-400">{idx + 1}</TableCell>
                  <TableCell className="font-mono font-medium text-xs">{c.mpn}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${lifecycleColor(c.lifecycle)}`}>{c.lifecycle || '-'}</Badge></TableCell>
                  <TableCell className="text-xs">{c.yteol ? <span className={c.yteol <= 24 ? 'text-red-600 font-semibold' : c.yteol <= 60 ? 'text-yellow-600 font-medium' : 'text-gray-600'}>{c.yteol}mo</span> : '-'}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${riskColor(rd.supply)}`}>{rd.supply || '-'}</Badge></TableCell>
                  <TableCell className="text-xs">{c.single_source ? <Badge className="text-[10px] bg-red-100 text-red-800">Yes</Badge> : <span className="text-gray-400">No</span>}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${riskColor(rd.compliance)}`}>{rd.compliance || '-'}</Badge></TableCell>
                  <TableCell><Badge className={`text-[10px] ${riskColor(rd.geopolitical)}`}>{rd.geopolitical || '-'}</Badge></TableCell>
                  <TableCell className="text-xs">{c.lead_time_days || (c.lead_time_weeks ? c.lead_time_weeks * 7 : '-')}d</TableCell>
                  <TableCell><Badge className={`text-[10px] font-bold ${riskColor(c.risk_score)}`}>{c.risk_score || '-'}</Badge></TableCell>
                </TableRow>
              );
              if (viewTab === 'compliance') return (
                <TableRow key={idx} className={`text-sm ${!c.matched ? 'bg-red-50/50' : ''}`} data-testid={`bom-comp-${idx}`}>
                  <TableCell className="text-xs text-gray-400">{idx + 1}</TableCell>
                  <TableCell className="font-mono font-medium text-xs">{c.mpn}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${riskColor(c.environmental_compliance)}`}>{c.environmental_compliance || '-'}</Badge></TableCell>
                  <TableCell>{comp.eu_rohs ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <X className="h-3.5 w-3.5 text-red-500" />}</TableCell>
                  <TableCell>{comp.china_rohs ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <X className="h-3.5 w-3.5 text-red-500" />}</TableCell>
                  <TableCell>{comp.reach ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <X className="h-3.5 w-3.5 text-red-500" />}</TableCell>
                  <TableCell>{comp.pfas ? <Badge className="bg-red-100 text-red-800 text-[10px]">Non-Compliant</Badge> : <Badge className="bg-green-100 text-green-800 text-[10px]">Compliant</Badge>}</TableCell>
                  <TableCell className="text-xs text-gray-600">{c.qualifications || '-'}</TableCell>
                  <TableCell className="text-xs">{c.temperature_grade || '-'}</TableCell>
                  <TableCell className="text-xs">{c.temperature_range || '-'}</TableCell>
                </TableRow>
              );
              if (viewTab === 'alternates') return (
                <TableRow key={idx} className="text-sm" data-testid={`bom-alt-${idx}`}>
                  <TableCell className="text-xs text-gray-400">{idx + 1}</TableCell>
                  <TableCell className="font-mono font-medium text-xs">{c.mpn}</TableCell>
                  <TableCell className="font-mono text-xs">{alt.mpn ? <button className="text-blue-600 hover:underline" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}`)}>{alt.mpn}</button> : <span className="text-gray-400">None</span>}</TableCell>
                  <TableCell className="text-xs text-gray-600">{alt.manufacturer || '-'}</TableCell>
                  <TableCell>{alt.type ? <Badge variant="outline" className="text-[10px]">{alt.type}</Badge> : '-'}</TableCell>
                  <TableCell className="text-xs">{alt.eu_rohs !== undefined ? (alt.eu_rohs ? <span className="text-green-700">Yes</span> : <span className="text-red-600">No</span>) : <span className="text-green-700">Yes</span>}</TableCell>
                  <TableCell className="text-xs">{alt.china_rohs !== undefined ? (alt.china_rohs ? <span className="text-green-700">Yes</span> : <span className="text-red-600">No</span>) : <span className="text-green-700">Yes</span>}</TableCell>
                  <TableCell className="text-xs">{alt.reach !== undefined ? (alt.reach ? <span className="text-green-700">Yes</span> : <span className="text-red-600">No</span>) : <span className="text-green-700">Yes</span>}</TableCell>
                  <TableCell className="text-xs">{alt.pfas ? <span className="text-green-700">Yes</span> : <span className="text-gray-400">N/A</span>}</TableCell>
                  <TableCell className="text-xs">{alt.temperature_grade || c.temperature_grade || '-'}</TableCell>
                  <TableCell className="text-xs">{alt.temperature_range || c.temperature_range || '-'}</TableCell>
                  <TableCell className="text-xs"><div className="max-w-[100px] truncate" title={alt.qualifications || c.qualifications || ''}>{alt.qualifications || c.qualifications || '-'}</div></TableCell>
                  <TableCell className="text-xs font-medium">{alt.median_price ? `$${alt.median_price.toFixed(4)}` : '-'}</TableCell>
                  <TableCell>{alt.median_price_diff_pct != null ? <Badge className={`text-[10px] ${alt.median_price_diff_pct > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{alt.median_price_diff_pct > 0 ? '-' : '+'}{Math.abs(alt.median_price_diff_pct)}%</Badge> : '-'}</TableCell>
                </TableRow>
              );
              return null;
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

/* ──── Main Component ──── */
const DesignProject = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configCollapsed, setConfigCollapsed] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showRfq, setShowRfq] = useState(false);
  const [assignData, setAssignData] = useState({ type: 'compliance_review', assignee: '', notes: '', mpns: [] });
  const [rfqMpns, setRfqMpns] = useState([]);
  const [rfqQty, setRfqQty] = useState(1000);
  const [actionLoading, setActionLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const branding = useBranding?.() || {};

  const fetchProject = useCallback(async () => {
    try {
      const res = await api.get(`/design/projects/${projectId}`);
      if (res.data.error) { toast.error('Project not found'); navigate('/design-center'); return; }
      setProject(res.data);
    } catch { toast.error('Failed to load project'); }
    setLoading(false);
  }, [projectId, navigate]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const fetchRecommendations = useCallback(async (regen = false) => {
    setRecoLoading(true);
    try {
      const res = await api.get(`/design/projects/${projectId}/recommendations${regen ? '?regenerate=true' : ''}`);
      setRecommendations(res.data);
    } catch { toast.error('Failed to load recommendations'); }
    setRecoLoading(false);
  }, [projectId]);



  useEffect(() => {
    if (!project || project.status !== 'processing') return;
    const poll = setInterval(async () => {
      try {
        const res = await api.get(`/design/projects/${projectId}`);
        if (res.data.status === 'ready' || res.data.status === 'failed') {
          setProject(res.data); clearInterval(poll);
          if (res.data.status === 'ready') toast.success('BOM processing complete');
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(poll);
  }, [project?.status, projectId]);

  const addComponent = async (mpn, quantity, refDes) => {
    try {
      const res = await api.post(`/design/projects/${projectId}/components`, { mpn, quantity, ref_designator: refDes });
      if (res.data.error) { toast.error(res.data.message || res.data.error); return; }
      toast.success(`${mpn} added to BOM`); fetchProject();
    } catch { toast.error('Failed to add component'); }
  };

  const removeComponent = async (idx) => {
    try { await api.delete(`/design/projects/${projectId}/components/${idx}`); toast.success('Component removed'); fetchProject(); }
    catch { toast.error('Failed to remove'); }
  };

  const editComponent = async (idx, updates) => {
    try { await api.put(`/design/projects/${projectId}/components/${idx}`, updates); toast.success('Updated'); fetchProject(); }
    catch { toast.error('Failed to update'); }
  };

  const exportCSV = () => {
    const url = `${api.defaults.baseURL}/design/projects/${projectId}/export/csv`;
    window.open(url, '_blank');
    toast.success('Exporting validation report...');
  };

  const downloadTemplate = () => {
    const url = `${api.defaults.baseURL}/design/template/download`;
    window.open(url, '_blank');
    toast.success('Downloading BOM template...');
  };

  const toggleRfqMpn = (mpn) => {
    setRfqMpns(prev => prev.includes(mpn) ? prev.filter(m => m !== mpn) : [...prev, mpn]);
  };

  const exportPDF = () => {
    if (!project) return;
    const sc = project.health_scorecard || {};
    const comps = project.components || [];
    const matched = comps.filter(c => c.matched);
    const doc = createBrandedPDF(`Design Validation Report — ${project.name}`, 'BOM Health & Compliance Analysis', branding);
    let y = doc._startY;

    // Health Scorecard
    y = addSectionTitle(doc, 'BOM Health Scorecard', y);
    y = addMetricCards(doc, [
      { label: 'Grade', value: sc.grade || 'N/A' },
      { label: 'Match Rate', value: `${sc.match_rate || 0}%` },
      { label: 'Lifecycle Risk', value: `${sc.lifecycle_risk_pct || 0}%` },
      { label: 'Compliance', value: `${sc.compliance_pct || 0}%` },
    ], y);

    // BOM Components Table
    y = addSectionTitle(doc, `BOM Components (${comps.length})`, y);
    y = addTable(doc,
      ['#', 'MPN', 'Manufacturer', 'Lifecycle', 'Risk', 'Median', 'Qty', 'Lead Time'],
      comps.map((c, i) => [
        i + 1, c.mpn, c.manufacturer || '-', c.lifecycle || '-',
        c.risk_score || '-', c.matched ? `$${(c.median_price || 0).toFixed(4)}` : '-',
        c.quantity || 1, c.lead_time_days ? `${c.lead_time_days}d` : '-',
      ]), y);
    y = doc.lastAutoTable?.finalY + 8 || y + 10;

    // Lifecycle Alerts
    const alerts = comps.filter(c => c.matched && ['NRND', 'EOL', 'Obsolete', 'LTB'].includes(c.lifecycle));
    if (alerts.length > 0) {
      if (y > 240) { doc.addPage(); y = 20; }
      y = addSectionTitle(doc, `Lifecycle Alerts (${alerts.length})`, y);
      y = addTable(doc,
        ['MPN', 'Status', 'YTEOL', 'Replacement', 'Type'],
        alerts.map(c => {
          const alt = (c.alternates || [])[0];
          return [c.mpn, c.lifecycle, `${c.yteol}mo`, alt?.mpn || 'None', alt?.type || '-'];
        }), y);
      y = doc.lastAutoTable?.finalY + 8 || y + 10;
    }

    // Compliance Summary
    if (y > 240) { doc.addPage(); y = 20; }
    y = addSectionTitle(doc, 'Compliance Summary', y);
    y = addTable(doc,
      ['MPN', 'EU-RoHS', 'CN-RoHS', 'REACH', 'FPAs', 'Qualifications', 'Temp Grade'],
      matched.map(c => {
        const co = c.compliance || {};
        return [c.mpn, co.eu_rohs ? 'Yes' : 'No', co.china_rohs ? 'Yes' : 'No',
          co.reach ? 'Yes' : 'No', co.pfas ? 'Yes' : 'No', c.qualifications || '-', c.temperature_grade || '-'];
      }), y);

    // AI Recommendations
    if (recommendations?.recommendations?.length > 0) {
      y = doc.lastAutoTable?.finalY + 8 || y + 10;
      if (y > 240) { doc.addPage(); y = 20; }
      y = addSectionTitle(doc, 'AI Recommendations', y);
      y = addTable(doc,
        ['#', 'Priority', 'Recommendation', 'Components', 'Action'],
        recommendations.recommendations.slice(0, 10).map(r => [
          String(r.id), (r.priority || '').toUpperCase(), r.title,
          (r.affected_components || []).slice(0, 2).join(', '), r.action_type || '-',
        ]),
        y
      );
    }

    downloadPDF(doc, `${project.name.replace(/\s+/g, '_')}_Validation_Report.pdf`);
    toast.success('PDF report downloaded');
  };

  const handoffToProcurement = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/design/projects/${projectId}/handoff`, { name: `${project.name} - Procurement` });
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      fetchProject();
    } catch (e) { toast.error(e.response?.data?.detail || 'Handoff failed'); }
    finally { setActionLoading(false); }
  };

  const submitRfq = async () => {
    if (rfqMpns.length === 0) { toast.error('Select at least one component'); return; }
    setActionLoading(true);
    try {
      const res = await api.post(`/design/projects/${projectId}/rfq`, { mpns: rfqMpns, quantity: rfqQty });
      toast.success(res.data.message || 'RFQ submitted'); setShowRfq(false); setRfqMpns([]);
    } catch { toast.error('Failed to submit RFQ'); }
    setActionLoading(false);
  };

  const submitAssign = async () => {
    if (!assignData.assignee) { toast.error('Please enter an assignee'); return; }
    setActionLoading(true);
    try {
      const mpns = assignData.mpns.length > 0 ? assignData.mpns : components.filter(c => c.matched).map(c => c.mpn);
      const res = await api.post(`/design/projects/${projectId}/assign`, { ...assignData, mpns });
      toast.success(res.data.message || 'Action assigned'); setShowAssign(false);
    } catch { toast.error('Failed to assign action'); }
    setActionLoading(false);
  };

  if (loading) {
    return (<Layout title="Design Project"><div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading project...</div></Layout>);
  }
  if (!project) return null;

  const components = project.components || [];
  const isNewDesign = project.workflow === 'new_design';
  const isProcessing = project.status === 'processing';
  const scorecard = project.health_scorecard;
  const lifecycleAlerts = components.filter(c => c.matched && ['NRND', 'EOL', 'Obsolete', 'LTB'].includes(c.lifecycle));

  return (
    <Layout title={project.name} subtitle={isNewDesign ? 'Build and evaluate your design BOM' : 'Validated BOM analysis'} breadcrumbs={[
      { label: 'Design Review', path: '/design-center/validate' },
      { label: project.name || 'Project Detail' },
    ]}>
      <div className="space-y-4" data-testid="design-project-page">
        {/* Back + Actions Bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" className="text-gray-500 gap-1 -ml-2" onClick={() => navigate('/design-center/validate')} data-testid="back-to-design-center">
            <ArrowLeft className="h-4 w-4" /> Design Review
          </Button>
          <div className="flex items-center gap-2">
            {!isProcessing && components.length > 0 && (
              <>
                <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={exportCSV} data-testid="btn-export-csv">
                  <FileText className="h-3 w-3" /> CSV
                </Button>
                <Button size="sm" variant="outline" className="gap-1 text-xs text-red-700" onClick={exportPDF} data-testid="btn-export-pdf">
                  <FileText className="h-3 w-3" /> PDF Report
                </Button>
                <Button size="sm" variant="outline" className="gap-1 text-xs text-purple-700" onClick={() => setShowAssign(true)} data-testid="btn-assign">
                  <UserPlus className="h-3 w-3" /> Assign Action
                </Button>
                {!project.handoff_bom_id ? (
                  <Button size="sm" className="gap-1 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={handoffToProcurement} disabled={actionLoading} data-testid="btn-handoff">
                    {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3" />} Send to Procurement
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1 text-xs text-green-700 border-green-300" onClick={() => navigate(`/bom/${project.handoff_bom_id}`)} data-testid="btn-view-procurement">
                    <Truck className="h-3 w-3" /> View Procurement BOM
                  </Button>
                )}
              </>
            )}
            <Badge className={project.status === 'ready' ? 'bg-green-100 text-green-800' : project.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
              {project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Processing State */}
        {isProcessing && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-10 text-center">
              <Loader2 className="h-10 w-10 text-green-600 animate-spin mx-auto mb-3" />
              <h3 className="text-lg font-bold text-gray-900">Processing BOM...</h3>
              <p className="text-gray-500 text-sm mt-1">Matching MPNs and enriching data. This will take a few seconds.</p>
            </CardContent>
          </Card>
        )}

        {/* Ready / Active State */}
        {!isProcessing && (
          <>
            <HealthScorecard scorecard={scorecard} components={components} project={project} />

            {/* Lifecycle Alerts Banner */}
            {lifecycleAlerts.length > 0 && (
              <Card className="bg-amber-50 border-amber-300 shadow-sm" data-testid="lifecycle-alerts">
                <CardHeader className="py-2 px-4 border-b border-amber-200">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-4 w-4" /> Lifecycle Alerts ({lifecycleAlerts.length} parts at risk)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50/50 text-xs">
                        <TableHead className="text-xs">MPN</TableHead>
                        <TableHead className="text-xs">Manufacturer</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">YTEOL</TableHead>
                        <TableHead className="text-xs">Suggested Replacement</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Savings</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lifecycleAlerts.map((c, i) => {
                        const alt = (c.alternates || [])[0];
                        return (
                          <TableRow key={i} data-testid={`lc-alert-${i}`}>
                            <TableCell className="font-mono text-xs font-medium">
                              <button className="text-blue-600 hover:underline" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(c.mpn)}`)}>{c.mpn}</button>
                            </TableCell>
                            <TableCell className="text-xs text-gray-600">{c.manufacturer}</TableCell>
                            <TableCell><Badge className={`text-[10px] ${lifecycleColor(c.lifecycle)}`}>{c.lifecycle}</Badge></TableCell>
                            <TableCell className="text-xs">{c.yteol}mo</TableCell>
                            <TableCell className="font-mono text-xs">
                              {alt ? <button className="text-blue-600 hover:underline" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}`)}>{alt.mpn} <span className="text-gray-400">({alt.manufacturer})</span></button> : <span className="text-gray-400">No replacement found</span>}
                            </TableCell>
                            <TableCell>{alt?.type ? <Badge variant="outline" className="text-[10px]">{alt.type}</Badge> : '-'}</TableCell>
                            <TableCell>{alt?.median_price_diff_pct ? <Badge className={`text-[10px] ${alt.median_price_diff_pct > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{alt.median_price_diff_pct > 0 ? '' : '+'}{alt.median_price_diff_pct > 0 ? '-' : ''}{Math.abs(alt.median_price_diff_pct)}%</Badge> : '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Match Summary Cards (Validation workflow only) */}
            {!isNewDesign && project.match_summary && (
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-white border-gray-200 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-xs text-gray-500 uppercase">Total Parts</p><p className="text-2xl font-bold text-gray-900">{project.match_summary.total}</p></CardContent></Card>
                <Card className="bg-green-50 border-green-200 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-xs text-green-600 uppercase">Matched</p><p className="text-2xl font-bold text-green-700">{project.match_summary.matched}</p><p className="text-xs text-green-600">{((project.match_summary.matched / project.match_summary.total) * 100).toFixed(0)}%</p></CardContent></Card>
                <Card className="bg-red-50 border-red-200 shadow-sm"><CardContent className="py-3 px-4 text-center"><p className="text-xs text-red-600 uppercase">Unmatched</p><p className="text-2xl font-bold text-red-700">{project.match_summary.unmatched}</p><p className="text-xs text-red-600">{project.match_summary.unmatched > 0 ? 'Need review' : 'All resolved'}</p></CardContent></Card>
              </div>
            )}

            {/* BOM Components Table */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-900 text-base">BOM Components</CardTitle>
                  {isNewDesign && (
                    <Button size="sm" variant="outline" className="gap-1 text-blue-600" onClick={() => setShowSearch(!showSearch)} data-testid="toggle-search-btn">
                      <Plus className="h-3.5 w-3.5" /> Add Component
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isNewDesign && showSearch && <div className="mb-4"><SearchPanel onAdd={addComponent} /></div>}
                <BOMTable components={components} onRemove={removeComponent} onEdit={editComponent}
                  showConfidence={!isNewDesign} onSelectForRfq={toggleRfqMpn} selectedRfq={rfqMpns} />
              </CardContent>
            </Card>

            {/* Unmatched Parts Resolution (Validation workflow only) */}
            {!isNewDesign && components.filter(c => !c.matched).length > 0 && (
                <Card className="bg-red-50 border-red-200 shadow-sm" data-testid="unmatched-parts-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-red-800 text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Unmatched Parts</CardTitle>
                    <CardDescription className="text-red-600">These parts could not be matched. Review suggestions below and resolve each one.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {components.filter(c => !c.matched).map((c, i) => {
                        const suggestions = c.closest_matches || [];
                        return (
                          <div key={i} className="bg-white rounded-lg border border-red-200 overflow-hidden" data-testid={`unmatched-${i}`}>
                            <div className="flex items-center gap-3 p-3 border-b border-red-100">
                              <Badge className="bg-red-100 text-red-800 text-[10px]">No Match</Badge>
                              <span className="font-mono font-medium text-sm">{c.mpn}</span>
                              {c.cpn && <span className="text-xs text-gray-400">CPN: {c.cpn}</span>}
                              <span className="text-xs text-gray-500">Qty: {c.quantity || c.annual_requirement || 1}</span>
                            </div>
                            {suggestions.length > 0 ? (
                              <div className="p-3">
                                <p className="text-xs text-gray-500 mb-2 font-medium">Did you mean one of these?</p>
                                <div className="space-y-1.5">
                                  {suggestions.map((s, j) => (
                                    <div key={j} className="flex items-center gap-3 p-2 rounded-md border border-gray-200 hover:bg-green-50 hover:border-green-200 transition-colors group" data-testid={`suggestion-${i}-${j}`}>
                                      <div className="flex-1 min-w-0">
                                        <span className="font-mono font-medium text-sm text-blue-700">{s.mpn}</span>
                                        <span className="text-xs text-gray-500 ml-2">{s.manufacturer}</span>
                                        <span className="text-xs text-gray-400 ml-2">{s.category}</span>
                                        {s.lifecycle && <Badge className={`text-[9px] ml-2 ${lifecycleColor(s.lifecycle)}`}>{s.lifecycle}</Badge>}
                                      </div>
                                      {s.median_price > 0 && <span className="text-xs text-gray-500">${s.median_price.toFixed(4)}</span>}
                                      <Button size="sm" variant="outline"
                                        className="h-6 text-[10px] px-2 text-green-700 border-green-300 hover:bg-green-100 opacity-80 group-hover:opacity-100"
                                        data-testid={`btn-use-${i}-${j}`}
                                        onClick={async () => {
                                          try {
                                            await api.post(`/design/projects/${project.project_id}/resolve-unmatched`, {
                                              original_mpn: c.mpn, replacement_mpn: s.mpn,
                                            });
                                            toast.success(`Resolved: ${c.mpn} → ${s.mpn}`);
                                            fetchProject();
                                          } catch { toast.error('Failed to resolve'); }
                                        }}>
                                        Use This
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="p-3 text-xs text-gray-400 italic">No similar parts found in database. Consider adding this part manually.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
            )}

            {/* Recommendations Section */}
            {!isNewDesign && components.filter(c => c.matched).length > 0 && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="recommendations-section">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-gray-900 text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" /> AI Recommendations
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {recommendations && !recoLoading && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1"
                          onClick={() => fetchRecommendations(true)} data-testid="btn-regen-reco">
                          <RefreshCw className="h-3 w-3" /> Regenerate
                        </Button>
                      )}
                      {!recommendations && !recoLoading && (
                        <Button size="sm" className="h-7 text-[10px] gap-1 bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => fetchRecommendations()} data-testid="btn-get-reco">
                          <Sparkles className="h-3 w-3" /> Generate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {recoLoading ? (
                    <div className="flex items-center justify-center py-8 text-gray-400">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Analyzing BOM for risks, compliance gaps and alternates...
                    </div>
                  ) : recommendations?.recommendations?.length > 0 ? (
                    <div className="space-y-3">
                      {recommendations.summary && (
                        <p className="text-sm text-gray-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5" data-testid="reco-summary">{recommendations.summary}</p>
                      )}
                      {recommendations.recommendations.map((r) => {
                        const catColors = { risk: 'bg-red-100 text-red-800', compliance: 'bg-orange-100 text-orange-800', alternates: 'bg-blue-100 text-blue-800', savings: 'bg-green-100 text-green-800' };
                        const prioColors = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800' };
                        return (
                          <div key={r.id} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors" data-testid={`reco-${r.id}`}>
                            <div className="flex items-start gap-2">
                              <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                                <Badge className={`text-[9px] ${prioColors[r.priority] || prioColors.medium}`}>{r.priority?.toUpperCase()}</Badge>
                                <Badge className={`text-[9px] ${catColors[r.category] || catColors.risk}`}>{r.category}</Badge>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{r.title}</p>
                                <p className="text-xs text-gray-600 mt-1">{r.description}</p>
                                {r.affected_components?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {r.affected_components.map(mpn => (
                                      <Badge key={mpn} variant="outline" className="text-[9px] font-mono">{mpn}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : recommendations ? (
                    <p className="text-center py-6 text-gray-400 text-sm">{recommendations.summary || 'No recommendations generated.'}</p>
                  ) : (
                    <p className="text-center py-6 text-gray-400 text-sm">Click "Generate" to get AI-powered risk, compliance and alternates recommendations for your BOM.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── Assign Action Dialog ─── */}
        <Dialog open={showAssign} onOpenChange={setShowAssign}>
          <DialogContent className="max-w-md" data-testid="assign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base"><UserPlus className="h-4 w-4 text-purple-600" /> Assign Action</DialogTitle>
              <DialogDescription className="text-xs">Assign a compliance or lifecycle review to a team member.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Action Type</Label>
                <Select value={assignData.type} onValueChange={v => setAssignData(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="h-8 mt-1" data-testid="assign-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compliance_review">Compliance Review</SelectItem>
                    <SelectItem value="lifecycle_review">Lifecycle Review</SelectItem>
                    <SelectItem value="cost_review">Cost Review</SelectItem>
                    <SelectItem value="engineering_approval">Engineering Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assignee (Email)</Label>
                <Input value={assignData.assignee} onChange={e => setAssignData(p => ({ ...p, assignee: e.target.value }))}
                  placeholder="engineer@company.com" className="h-8 mt-1" data-testid="assign-email" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={assignData.notes} onChange={e => setAssignData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Review NRND parts for replacement..." className="mt-1 h-20" data-testid="assign-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowAssign(false)}>Cancel</Button>
              <Button size="sm" onClick={submitAssign} disabled={actionLoading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-assign">
                {actionLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UserPlus className="h-3 w-3 mr-1" />} Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── RFQ Dialog ─── */}
        <Dialog open={showRfq} onOpenChange={setShowRfq}>
          <DialogContent className="max-w-lg" data-testid="rfq-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base"><Send className="h-4 w-4 text-blue-600" /> Request for Quote</DialogTitle>
              <DialogDescription className="text-xs">Select components and submit an RFQ to distributors.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Target Quantity</Label>
                <Input type="number" value={rfqQty} onChange={e => setRfqQty(parseInt(e.target.value) || 1000)}
                  className="h-8 mt-1 w-40" data-testid="rfq-qty" />
              </div>
              <div>
                <Label className="text-xs">Components ({rfqMpns.length} selected)</Label>
                <div className="mt-1 max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {components.filter(c => c.matched).map((c, i) => (
                    <label key={i} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input type="checkbox" checked={rfqMpns.includes(c.mpn)} onChange={() => toggleRfqMpn(c.mpn)} className="h-3 w-3" />
                      <span className="font-mono font-medium">{c.mpn}</span>
                      <span className="text-gray-400">{c.manufacturer}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRfqMpns(components.filter(c => c.matched).map(c => c.mpn))} className="text-xs">Select All</Button>
                <Button variant="outline" size="sm" onClick={() => setRfqMpns([])} className="text-xs">Clear</Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowRfq(false)}>Cancel</Button>
              <Button size="sm" onClick={submitRfq} disabled={actionLoading || rfqMpns.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-rfq">
                {actionLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />} Submit RFQ ({rfqMpns.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DesignProject;
